<template>
  <div class="hc-compact-page medical-template-page">
    <PageHeader
      title="Cetak Blanko Rekam Medis"
      subtitle="Pilih pasien, tinjau blanko, lalu cetak atau simpan sebagai PDF"
      icon="fas fa-file-medical"
    >
      <template #actions>
        <b-button class="medical-template-back" variant="outline-secondary" @click="$router.push('/patient/list')">
          <i class="fas fa-arrow-left"></i><span>Kembali ke Pasien</span>
        </b-button>
      </template>
    </PageHeader>

    <section class="medical-template-selector">
      <div class="medical-template-selector__intro">
        <div class="medical-template-selector__icon"><i class="fas fa-user-check"></i></div>
        <div>
          <span>Langkah 1</span>
          <h2>Pilih pasien</h2>
          <p>Data identitas pasien akan otomatis dimasukkan ke dalam blanko rekam medis.</p>
        </div>
      </div>

      <div class="medical-template-selector__fields hc-no-form-icons">
        <div class="medical-template-field">
          <label for="input-trigger-modal-patient">Pasien utama</label>
          <button
            id="input-trigger-modal-patient"
            type="button"
            class="medical-patient-picker"
            @click="$bvModal.show('modal-patient')"
          >
            <span class="medical-patient-picker__icon"><i class="fas fa-search"></i></span>
            <span class="medical-patient-picker__value">
              <small>{{ display.patient_name ? 'Pasien terpilih' : 'Cari pasien' }}</small>
              <strong>{{ display.patient_name || 'Klik untuk mencari dan memilih pasien' }}</strong>
            </span>
            <i class="fas fa-chevron-right medical-patient-picker__arrow"></i>
          </button>
        </div>

        <div v-if="subPatient.length > 1" class="medical-template-field">
          <label for="input-sub-patient">Sub pasien</label>
          <treeselect
            id="input-sub-patient"
            v-model="subPatientId"
            placeholder="Pilih sub pasien (opsional)"
            :options="subPatient"
            @input="setSubPatient"
          />
        </div>
      </div>
    </section>

    <div v-if="loadError" class="medical-template-alert" role="alert">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ loadError }}</span>
      <button type="button" @click="pagination">Coba lagi</button>
    </div>

    <div v-if="loading" class="medical-template-loading">
      <b-spinner small variant="primary" />
      <span>Menyiapkan blanko rekam medis...</span>
    </div>

    <section v-else-if="dataLoad" class="medical-template-preview">
      <header class="medical-template-preview__header">
        <div>
          <span>Langkah 2</span>
          <h2>Pratinjau Blanko</h2>
          <p>{{ display.patient_name }} · {{ data.patient_id || 'Nomor rekam medis belum tersedia' }}</p>
        </div>
        <div class="medical-template-actions">
          <b-button variant="outline-primary" @click="btnPrintOnClick">
            <i class="fas fa-print"></i><span>Cetak</span>
          </b-button>
          <b-button variant="primary" :disabled="downloadProgress > 0 && downloadProgress < 100" @click="btnDownloadOnClick">
            <b-spinner v-if="downloadProgress > 0 && downloadProgress < 100" small />
            <i v-else class="fas fa-file-download"></i>
            <span>{{ downloadProgress > 0 && downloadProgress < 100 ? `Menyiapkan ${downloadProgress}%` : 'Unduh PDF' }}</span>
          </b-button>
        </div>
      </header>

      <div class="medical-template-preview__canvas">
        <Print :data="data" :items="items" />
      </div>

      <vue-html2pdf
        ref="html2Pdf"
        :show-layout="false"
        :float-layout="true"
        :enable-download="true"
        :preview-modal="true"
        :paginate-elements-by-height="1400"
        :filename="'Blanko Rekam Medis - ' + display.patient_name"
        :pdf-quality="1"
        :manual-pagination="false"
        pdf-format="a4"
        pdf-orientation="portrait"
        pdf-content-width="800px"
        @progress="onProgress"
        @hasStartedGeneration="hasStartedGeneration"
        @hasGenerated="hasGenerated"
      >
        <section slot="pdf-content">
          <Print v-if="dataLoad" :data="data" :items="items" />
        </section>
      </vue-html2pdf>
    </section>

    <section v-else class="medical-template-empty">
      <div><i class="far fa-file-alt"></i></div>
      <strong>Blanko siap dibuat</strong>
      <span>Pilih pasien di atas untuk menampilkan pratinjau blanko rekam medis.</span>
    </section>

    <Modal source="template-print" @chosenPatient="setPatient" @submitedPatient="setPatient" />
  </div>
</template>

<script>
import PageHeader from "@/component/general/PageHeader.vue";
import Print from "@/component/patients/ThemplateMedicalRecord.vue";
import VueHtml2pdf from "vue-html2pdf";
import module from "@/core/modules/CrudModule.js";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import Modal from "@/component/general/ModalPatient.vue";

export default {
  components: { PageHeader, Print, VueHtml2pdf, Modal },

  data() {
    return {
      data: {},
      items: [],
      dataLoad: false,
      loading: false,
      loadError: "",
      downloadProgress: 0,
      filter: { start_date: null, end_date: null, id: null },
      display: { patient_name: "" },
      subPatient: [],
      subPatientId: null,
    };
  },

  methods: {
    async setPatient(value) {
      this.subPatientId = null;
      this.subPatient = [];
      this.filter.id = value.id;
      this.display.patient_name = value.name || value.patient_name;
      this.$bvModal.hide("modal-patient");
      await Promise.all([this.getSubPatientOption(), this.pagination()]);
    },
    async getSubPatientOption() {
      const response = await module.setTreeSelect("sub-patients/" + this.filter.id);
      if (response && response.state === "success" && Array.isArray(response.data)) {
        this.subPatient = response.data;
      }
    },
    async pagination() {
      if (!this.filter.id) return;
      this.loading = true;
      this.loadError = "";
      try {
        await this.getPatient();
        this.dataLoad = true;
      } catch (error) {
        this.dataLoad = false;
        this.loadError = "Data pasien belum dapat dimuat. Periksa koneksi lalu coba lagi.";
      } finally {
        this.loading = false;
      }
    },
    async setSubPatient() {
      await this.pagination();
    },
    async getPatient() {
      const response = await module.get("patients/" + this.filter.id);
      if (!response) throw new Error("Patient not found");
      this.data = { ...response };
      if (this.subPatientId) {
        const selected = this.subPatient.find(item => item.id == this.subPatientId);
        if (selected) this.data.patient_name = `${this.data.patient_name} - ${selected.label}`;
      }
    },
    btnPrintOnClick() {
      this.$htmlToPaper("print");
    },
    btnDownloadOnClick() {
      this.downloadProgress = 1;
      this.$refs.html2Pdf.generatePdf();
    },
    onProgress(progress) {
      this.downloadProgress = Math.round(progress || 0);
    },
    hasStartedGeneration() {
      this.downloadProgress = Math.max(this.downloadProgress, 1);
    },
    hasGenerated() {
      this.downloadProgress = 0;
    },
  },

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Pasien", route: "/patient/list" },
      { title: "Cetak Blanko Rekam Medis" },
    ]);
  },
};
</script>

<style lang="scss" scoped>
.medical-template-page { color: #1e293b; }
.medical-template-back { min-height: 40px; display: inline-flex; align-items: center; gap: 7px; border-radius: 10px; font-size: 0.8rem; font-weight: 700; }
.medical-template-back i { margin: 0 !important; }

.medical-template-selector {
  display: grid; grid-template-columns: minmax(250px, 0.8fr) minmax(380px, 1.2fr); gap: 24px;
  margin-bottom: 14px; padding: 22px; border: 1px solid #dceae8; border-radius: 18px;
  background: radial-gradient(circle at 7% 18%, rgba(var(--skin-secondary-rgb),.1), transparent 34%), linear-gradient(135deg,#fff,#f7fbfb);
  box-shadow: 0 10px 28px rgba(var(--skin-deep-rgb),.05);
}
.medical-template-selector__intro { display: flex; align-items: flex-start; gap: 14px; }
.medical-template-selector__icon { width: 48px; height: 48px; flex: 0 0 48px; display: grid; place-items: center; border-radius: 13px; background: linear-gradient(135deg,var(--skin-primary),var(--skin-secondary)); color: #fff; box-shadow: 0 8px 18px rgba(var(--skin-secondary-rgb),.2); }
.medical-template-selector__intro span, .medical-template-preview__header > div > span { display: block; margin-bottom: 3px; color: var(--skin-secondary); font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.medical-template-selector__intro h2, .medical-template-preview__header h2 { margin: 0 0 4px; color: #1e293b; font-size: 16px; font-weight: 800; }
.medical-template-selector__intro p, .medical-template-preview__header p { margin: 0; color: #7a899a; font-size: 12px; line-height: 1.55; }
.medical-template-selector__fields { display: grid; gap: 13px; }
.medical-template-field label { display: block; margin-bottom: 6px; color: #4b5b6e; font-size: 11px; font-weight: 750; }
.medical-patient-picker { width: 100%; min-height: 54px; display: flex; align-items: center; gap: 11px; padding: 8px 12px; border: 1px solid #dce5ef; border-radius: 12px; background: #fff; text-align: left; transition: border-color .18s ease, box-shadow .18s ease; }
.medical-patient-picker:hover { border-color: var(--skin-secondary); box-shadow: 0 0 0 3px rgba(var(--skin-secondary-rgb),.1); }
.medical-patient-picker__icon { width: 34px; height: 34px; flex: 0 0 34px; display: grid; place-items: center; border-radius: 9px; background: var(--skin-soft); color: var(--skin-secondary); }
.medical-patient-picker__value { min-width: 0; flex: 1; }
.medical-patient-picker__value small, .medical-patient-picker__value strong { display: block; }
.medical-patient-picker__value small { color: #93a0ae; font-size: 9px; font-weight: 750; text-transform: uppercase; }
.medical-patient-picker__value strong { overflow: hidden; color: #344256; font-size: 12px; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
.medical-patient-picker__arrow { color: #9ba8b5; font-size: 10px; }
::v-deep .medical-template-field .vue-treeselect__control { height: 54px; border: 1px solid #dce5ef; border-radius: 12px; background: #fff; }
::v-deep .medical-template-field .vue-treeselect__placeholder,
::v-deep .medical-template-field .vue-treeselect__single-value { line-height: 52px; color: #657185; font-size: 12px; }

.medical-template-loading, .medical-template-alert { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; padding: 14px 17px; border-radius: 13px; background: #fff; font-size: 12px; font-weight: 700; }
.medical-template-loading { justify-content: center; min-height: 120px; border: 1px solid #e4ebf2; color: #64748b; }
.medical-template-alert { border: 1px solid #f0caca; background: #fff7f7; color: #a33f3f; }
.medical-template-alert button { margin-left: auto; border: 0; background: transparent; color: var(--skin-primary); font-weight: 800; }
.medical-template-preview { overflow: hidden; border: 1px solid #e4ebf2; border-radius: 18px; background: #fff; box-shadow: 0 10px 28px rgba(var(--skin-deep-rgb),.05); }
.medical-template-preview__header { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 17px 20px; border-bottom: 1px solid #e7edf4; background: #fafcfd; }
.medical-template-actions { display: flex; gap: 8px; }
.medical-template-actions .btn { min-height: 39px; display: inline-flex; align-items: center; gap: 7px; border-radius: 9px; font-size: 11px; font-weight: 750; }
.medical-template-actions i { margin: 0 !important; }
.medical-template-preview__canvas { display: flex; justify-content: center; padding: 28px; overflow: auto; background: #eef2f6; }
::v-deep .medical-template-preview__canvas > * { flex: 0 0 auto; }
.medical-template-empty { min-height: 230px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 25px; border: 1px dashed #d6e0e8; border-radius: 16px; background: #fafcfd; text-align: center; }
.medical-template-empty > div { width: 54px; height: 54px; display: grid; place-items: center; margin-bottom: 12px; border-radius: 15px; background: var(--skin-soft); color: var(--skin-secondary); font-size: 19px; }
.medical-template-empty strong { color: #405168; font-size: 13px; }
.medical-template-empty span { margin-top: 4px; color: #8d9bab; font-size: 11px; }

@media (max-width: 991.98px) { .medical-template-selector { grid-template-columns: 1fr; } }
@media (max-width: 767.98px) {
  .medical-template-selector { padding: 17px; }
  .medical-template-preview__header { align-items: stretch; flex-direction: column; }
  .medical-template-actions, .medical-template-actions .btn, .medical-template-back { width: 100%; }
  .medical-template-actions .btn, .medical-template-back { justify-content: center; }
  .medical-template-preview__canvas { justify-content: flex-start; padding: 14px; }
}
</style>
